import { Flex, Radio, Text } from '@mantine/core';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { LoanDefinitionType, SecurityType } from '@dealstack/shared/lib/generated';
import { TransactionVestingTermsDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/TransactionVestingTermsDrawer';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox';
import { useTransactionTable } from './useTransactionTable';

interface Props {
	getInvestmentReference: (
		investmentWorkflowType: string,
		isReleased: boolean,
		date: string
	) => string;
	getSecurityTypeTranslation: (type?: SecurityType | LoanDefinitionType) => string;
}

export const TransactionTable = ({ getInvestmentReference, getSecurityTypeTranslation }: Props) => {
	const {
		data,
		investmentsLoading,
		columns,
		vestingTerms,
		view,
		setView,
		showViewSwitch,
		onVestingTermApply,
		onVestingTermRemove,
		onVestingStartDateChange,
		vestingTermUpdateLoading,
		selectedTransaction,
		setSelectedTransaction,
		tMipManage,
		dataOperations,
	} = useTransactionTable({
		getInvestmentReference,
		getSecurityTypeTranslation,
	});

	return (
		<Flex direction="column" style={{ position: 'relative' }}>
			<Text fz="lg" fw={800} mt="2rem" mb="1rem">
				{tMipManage('investors.expanded_row.transactions')}
			</Text>

			{showViewSwitch && (
				<Radio.Group value={view} onChange={(value: typeof view) => setView(value)}>
					<Flex gap="1rem" mb="1rem">
						<Radio
							value="completed"
							label={tMipManage('investors.details.completed')}
						/>
						<Radio value="pending" label={tMipManage('investors.details.pending')} />
					</Flex>
				</Radio.Group>
			)}

			{data?.length || investmentsLoading ? (
				<MantineTable
					data={data}
					columns={columns}
					getRowId={(row) => row.id}
					dataOperations={dataOperations}
					manualSorting={false}
					enablePagination={false}
					enableSearchBar={false}
					enableTopToolbar={false}
					enableRowSelection={false}
					enableFilters={false}
					enableHiding={false}
					isLoading={investmentsLoading}
					mantineTableBodyRowProps={() => ({
						style: {
							height: 'unset',
						},
					})}
				/>
			) : (
				<Flex maw="60rem">
					<InfoBox
						iconVariant="information"
						alignContent="center"
						title={tMipManage('investors.details.alerts.transaction_title')}
						description={tMipManage('investors.details.alerts.transaction_description')}
					/>
				</Flex>
			)}

			<TransactionVestingTermsDrawer
				opened={!!selectedTransaction}
				onClose={() => {
					setSelectedTransaction(undefined);
				}}
				vestingTerms={vestingTerms || []}
				selectedTransaction={selectedTransaction}
				onVestingTermApply={onVestingTermApply}
				onVestingTermRemove={onVestingTermRemove}
				onVestingDateChange={onVestingStartDateChange}
				loading={vestingTermUpdateLoading}
			/>
		</Flex>
	);
};
